/**
 * API client.
 *
 * Every call attaches a fresh Clerk session token. Tokens are short-lived,
 * so caching one across a long-lived tab means requests start failing after
 * a few minutes - getToken() handles refresh internally, so we ask for it
 * per request rather than holding onto it.
 */

const BASE = import.meta.env.VITE_API_URL ?? 'https://api-uat.nimblybase.com'

/**
 * Types come from @nimblybase/contracts rather than being restated here.
 * They were previously hand-copied, which meant the dashboard could believe a
 * field existed that the API had stopped sending - and TypeScript would agree
 * with it right up to the runtime undefined.
 */
import type {
  Project, ApiKey, Member, Me, Invitation, Role, Organization, PendingInvitation,
  OAuthConnection, AccessToken, BackendDescription, SuitabilityFacts, SuitabilityPreview, ProjectPlanBody, ProjectPlanResponse,
} from '@nimblybase/contracts'

// Re-exported so pages import their types from the API client they call,
// rather than each reaching into the contract package independently.
export type {
  Project, ApiKey, Member, Me, Invitation, Role, Organization, PendingInvitation,
  AccessToken, CreateProjectBody, CreateApiKeyBody, CreateInvitationBody,
  CreateAccessTokenBody,
} from '@nimblybase/contracts'
export { ROLES } from '@nimblybase/contracts'

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message)
  }
}

type GetToken = () => Promise<string | null>
/** The active organization. Null while none is selected - onboarding, mostly. */
type GetOrgId = () => string | null

async function request<T>(
  getToken: GetToken,
  getOrgId: GetOrgId,
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const token = await getToken()
  if (!token) throw new ApiError(401, 'unauthorized', 'Not signed in')
  const orgId = getOrgId()

  const res = await fetch(`${BASE}${path}`, {
    ...init,
    headers: {
      Authorization: `Bearer ${token}`,
      // The active organization is a REQUEST, honoured only if the server
      // finds a membership. Organizations are ours - the token knows nothing
      // about them.
      ...(orgId ? { 'X-Organization-Id': orgId } : {}),
      ...(init.body ? { 'Content-Type': 'application/json' } : {}),
      ...init.headers,
    },
  })

  if (res.status === 204) return undefined as T

  const text = await res.text()
  const body = text ? safeJson(text) : null

  if (!res.ok) {
    // Surface the API's own error code and message. The API is careful not
    // to leak internals in them, so passing them through is safe and makes
    // failures actionable instead of "something went wrong".
    throw new ApiError(
      res.status,
      body?.error ?? 'error',
      humanMessage(res.status, body),
    )
  }
  return body as T
}

function safeJson(text: string): any {
  try { return JSON.parse(text) } catch { return null }
}

function humanMessage(status: number, body: any): string {
  if (body?.issues?.length) {
    return body.issues.map((i: any) => `${i.path?.join('.') ?? 'field'}: ${i.message}`).join(', ')
  }
  if (body?.message) return body.message
  switch (body?.error) {
    case 'slug_taken': return 'That slug is already in use. Slugs are globally unique because they become hostnames.'
    case 'invalid_slug': return `Invalid slug${body.detail ? ` (${body.detail.replace(/_/g, ' ')})` : ''}.`
    case 'invalid_name': return 'Name must be between 1 and 100 characters.'
    case 'already_a_member': return 'That person is already in this organization.'
    case 'already_invited': return 'They already have a pending invitation. Revoke it to send a new one.'
    case 'role_too_high': return 'You cannot invite someone to a role above your own.'
    case 'invalid_email': return 'That does not look like an email address.'
    case 'project_paused': return 'This project is paused. Resume it to use it again.'
    case 'project_suspended': return 'This project was suspended by NimblyBase. Contact support.'
    case 'not_pausable': return 'Only an active project can be paused.'
    case 'not_resumable': return 'Only a paused project can be resumed.'
    case 'not_found': return 'Not found.'
    case 'forbidden': return 'Your role does not allow that.'
    default: return `Request failed (${status}).`
  }
}

export function createApi(getToken: GetToken, getOrgId: GetOrgId = () => null) {
  const req = <T,>(path: string, init?: RequestInit) => request<T>(getToken, getOrgId, path, init)
  return {
    me: () => req<Me>('/v1/me'),
    listOrganizations: () => req<{ organizations: Organization[] }>('/v1/organizations'),
    createOrganization: (input: { name: string; slug?: string }) =>
      req<{ organization: Organization }>('/v1/organizations', {
        method: 'POST', body: JSON.stringify(input),
      }),
    myInvitations: () => req<{ invitations: PendingInvitation[] }>('/v1/me/invitations'),
    redeemInvitation: (id: string) =>
      req<{ organization: { id: string; role: string | null } }>(`/v1/invitations/${id}/redeem`, {
        method: 'POST', body: JSON.stringify({}),
      }),
    listMembers: () => req<{ members: Member[] }>('/v1/members'),
    listInvitations: () => req<{ invitations: Invitation[] }>('/v1/invitations'),
    createInvitation: (input: { email: string; role: Role }) =>
      req<{ invitation: Invitation; token: string }>('/v1/invitations', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    revokeInvitation: (id: string) =>
      req<void>(`/v1/invitations/${id}`, { method: 'DELETE' }),
    /**
     * The authorization an application is asking for.
     *
     * `search` is the client's own query string, passed through
     * untouched: PKCE challenge, state and scopes are the client's to
     * compose, and re-encoding them here is how a state stops matching.
     */
    describeAuthorization: (search: string) =>
      req<AuthorizationRequest>(`/oauth/authorization-request${search}`),
    decideAuthorization: (params: Record<string, string>, approved: boolean, reviewedOrgId: string) =>
      req<{ redirect_to: string }>('/oauth/authorization', {
        method: 'POST', body: JSON.stringify({ ...params, approved, organization_id: reviewedOrgId }),
      }),
    describeDeviceCode: (userCode: string) =>
      req<DeviceCodeRequest>(`/oauth/device-request?user_code=${encodeURIComponent(userCode)}`),
    decideDeviceCode: (userCode: string, approved: boolean, reviewedOrgId: string) =>
      req<{ approved: boolean }>('/oauth/device', {
        method: 'POST', body: JSON.stringify({ user_code: userCode, approved, organization_id: reviewedOrgId }),
      }),
    listProjects: () => req<{ projects: Project[] }>('/v1/projects'),
    assessProject: (facts: SuitabilityFacts) => req<SuitabilityPreview>('/v1/project-assessments/preview', { method: 'POST', body: JSON.stringify(facts) }),
    createProjectPlan: (body: ProjectPlanBody) => req<ProjectPlanResponse>('/v1/project-plans', { method: 'POST', body: JSON.stringify(body) }),
    getProjectPlan: (id: string) => req<ProjectPlanResponse>(`/v1/project-plans/${encodeURIComponent(id)}`),
    getProject: (id: string) => req<{ project: Project }>(`/v1/projects/${id}`),
    describeProject: (id: string) => req<BackendDescription>(`/v1/projects/${id}/describe`),
    createProject: (input: { name: string; slug?: string; databaseTier: 'd1' | 'neon'; planId?: string; factsFingerprint?: string }) =>
      req<{ project: Project }>('/v1/projects', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    deleteProject: (id: string) =>
      req<void>(`/v1/projects/${id}`, { method: 'DELETE' }),
    listKeys: (projectId: string) =>
      req<{ keys: ApiKey[] }>(`/v1/projects/${projectId}/keys`),
    createKey: (projectId: string, input: { name: string; type: 'anon' | 'service' }) =>
      req<{ key: ApiKey & { plaintext: string }; warning: string }>(
        `/v1/projects/${projectId}/keys`,
        { method: 'POST', body: JSON.stringify(input) },
      ),
    pauseProject: (id: string) =>
      req<{ project: { id: string; status: string } }>(`/v1/projects/${id}/pause`, { method: 'POST', body: '{}' }),
    resumeProject: (id: string) =>
      req<{ project: { id: string; status: string } }>(`/v1/projects/${id}/resume`, { method: 'POST', body: '{}' }),
    revokeKey: (keyId: string) =>
      req<void>(`/v1/keys/${keyId}`, { method: 'DELETE' }),

    // --- a project's own data -------------------------------------------
    // Forwarded by the control plane to the project's Worker, so the same
    // policy layer applies here as to a customer's app.
    projectSchema: (id: string) => req<{ schema: DbSchema }>(`/v1/projects/${id}/schema`),
    projectMigrations: (id: string) =>
      req<{ migrations: { id: number; appliedAt: string; statements: number }[] }>(`/v1/projects/${id}/migrations`),
    projectSql: (id: string, sql: string) =>
      req<SqlResult>(`/v1/projects/${id}/sql`, { method: 'POST', body: JSON.stringify({ sql, params: [] }) }),
    projectPolicies: (id: string) => req<{ policies: Policy[] }>(`/v1/projects/${id}/policies`),
    putProjectPolicy: (id: string, policy: unknown) =>
      req<{ policy: Policy }>(`/v1/projects/${id}/policies`, { method: 'POST', body: JSON.stringify(policy) }),
    deleteProjectPolicy: (id: string, policyId: number) =>
      req<void>(`/v1/projects/${id}/policies/${policyId}`, { method: 'DELETE' }),
    projectRows: (id: string, table: string, query: string) =>
      req<{ rows: Record<string, unknown>[]; count: number }>(
        `/v1/projects/${id}/rows/${encodeURIComponent(table)}${query}`),
    // Read and remove only. Deploying needs files, which is the CLI's job.
    site: (id: string) => req<SiteStatus>(`/v1/projects/${id}/site`),
    deleteSite: (id: string) => req<{ deleted: boolean }>(`/v1/projects/${id}/site`, { method: 'DELETE' }),
    listFunctions: (id: string) => req<{ functions: EdgeFunction[] }>(`/v1/projects/${id}/functions`),
    setFunctionSchedule: (id: string, name: string, schedule: string | null) =>
      req<{ function: EdgeFunction; message?: string }>(
        `/v1/projects/${id}/functions/${encodeURIComponent(name)}/schedule`,
        { method: 'POST', body: JSON.stringify({ schedule }) }),
    deleteFunction: (id: string, name: string) =>
      req<void>(`/v1/projects/${id}/functions/${encodeURIComponent(name)}`, { method: 'DELETE' }),

    aiModels: (id: string) =>
      req<{ models: ModelInfo[]; available: boolean }>(`/v1/projects/${id}/ai/models`),

    projectAuth: (id: string) => req<AuthStatus>(`/v1/projects/${id}/auth`),
    enableAuth: (id: string, trustedOrigins: string[]) =>
      req<AuthStatus>(`/v1/projects/${id}/auth`, {
        method: 'POST', body: JSON.stringify({ trustedOrigins }),
      }),
    disableAuth: (id: string) =>
      req<{ enabled: false; message: string }>(`/v1/projects/${id}/auth`, { method: 'DELETE' }),
    setAuthProvider: (id: string, provider: string, body: { clientId: string; clientSecret: string }) =>
      req<{ provider: AuthProvider; live: boolean; message: string }>(
        `/v1/projects/${id}/auth/providers/${provider}`, { method: 'PUT', body: JSON.stringify(body) }),
    deleteAuthProvider: (id: string, provider: string) =>
      req<{ removed: boolean; live: boolean; message: string }>(
        `/v1/projects/${id}/auth/providers/${provider}`, { method: 'DELETE' }),
    revokeAuthSessions: (id: string) =>
      req<{ revoked: boolean; sessions: number; message: string }>(
        `/v1/projects/${id}/auth/revoke`, { method: 'POST' }),
    realtimeTicket: (id: string, channel: string) =>
      req<{ channel: string; ticket: string; expiresAt: string; url: string }>(
        `/v1/projects/${id}/realtime/ticket`, { method: 'POST', body: JSON.stringify({ channel }) }),
    searchVectors: (id: string, body: { text: string; topK?: number }) =>
      req<{ matches?: { id: string; score: number; metadata: Record<string, unknown> | null }[] }>(
        `/v1/projects/${id}/vectors/query`, { method: 'POST', body: JSON.stringify(body) }),
    listAuthUsers: (id: string, q: { limit?: number; cursor?: string; search?: string } = {}) => {
      const params = new URLSearchParams()
      if (q.limit !== undefined) params.set('limit', String(q.limit))
      if (q.cursor) params.set('cursor', q.cursor)
      if (q.search) params.set('search', q.search)
      const query = params.toString()
      return req<EndUserPage>(`/v1/projects/${id}/auth/users${query ? `?${query}` : ''}`)
    },
    authEmailPreview: (id: string, kind: 'password_reset' | 'email_verification') =>
      req<{
        kind: string; subject: string; html: string; text: string
        deliverable: boolean; from?: string
      }>(`/v1/projects/${id}/auth/email-preview?kind=${kind}`),
    setAuthUserDisabled: (id: string, userId: string, body: { disabled: boolean; reason?: string }) =>
      req<{ disabled: boolean; sessions: number; message: string }>(
        `/v1/projects/${id}/auth/users/${encodeURIComponent(userId)}/disabled`,
        { method: 'POST', body: JSON.stringify(body) }),
    revokeAuthUser: (id: string, userId: string) =>
      req<{ revoked: boolean; sessions: number; message: string }>(
        `/v1/projects/${id}/auth/users/${encodeURIComponent(userId)}/revoke`, { method: 'POST' }),

    listDomains: (id: string) =>
      req<{ domains: CustomDomain[]; available: boolean }>(`/v1/projects/${id}/domains`),
    addDomain: (id: string, domain: string) =>
      req<CustomDomain>(`/v1/projects/${id}/domains`, {
        method: 'POST', body: JSON.stringify({ domain }),
      }),
    checkDomain: (id: string, domainId: string) =>
      req<CustomDomain>(`/v1/projects/${id}/domains/${encodeURIComponent(domainId)}/check`,
        { method: 'POST' }),
    deleteDomain: (id: string, domainId: string) =>
      req<{ disconnected: boolean; domain: string }>(
        `/v1/projects/${id}/domains/${encodeURIComponent(domainId)}`, { method: 'DELETE' }),

    // There is no getSecret. The API has no route behind one.
    listSecrets: (id: string) => req<{ secrets: Secret[] }>(`/v1/projects/${id}/secrets`),
    setSecret: (id: string, name: string, value: string) =>
      req<SecretWrite>(`/v1/projects/${id}/secrets`, {
        method: 'POST', body: JSON.stringify({ name, value }),
      }),
    deleteSecret: (id: string, name: string) =>
      req<SecretWrite>(`/v1/projects/${id}/secrets/${encodeURIComponent(name)}`, { method: 'DELETE' }),
    diagnoseProject: (id: string) => req<Diagnosis>(`/v1/projects/${id}/diagnose`),
    listFiles: (id: string, q: { prefix?: string; limit?: number; cursor?: string } = {}) => {
      const params = new URLSearchParams()
      if (q.prefix) params.set('prefix', q.prefix)
      if (q.limit !== undefined) params.set('limit', String(q.limit))
      if (q.cursor) params.set('cursor', q.cursor)
      const query = params.toString()
      return req<{
        objects: { key: string; size: number; uploaded: string; contentType: string | null }[]
        truncated: boolean
        cursor: string | null
      }>(`/v1/projects/${id}/files${query ? `?${query}` : ''}`)
    },
    listBuckets: (id: string) => req<{ buckets: StorageBucket[]; note: string }>(`/v1/projects/${id}/buckets`),
    putBucket: (id: string, name: string, body: { visibility: string; ownerScoped: boolean; adopt?: boolean }) =>
      req<{ bucket: StorageBucket; adopted: boolean; revokedSignedUrls: boolean; note?: string }>(
        `/v1/projects/${id}/buckets/${encodeURIComponent(name)}`,
        { method: 'PUT', body: JSON.stringify(body) }),
    deleteBucket: (id: string, name: string) =>
      req<{ name: string; removed: boolean; note: string }>(
        `/v1/projects/${id}/buckets/${encodeURIComponent(name)}`, { method: 'DELETE' }),
    projectLogs: (id: string, query: { since?: string; level?: string; route?: string; limit?: number }) => {
      const search = new URLSearchParams()
      if (query.since) search.set('since', query.since)
      if (query.level) search.set('level', query.level)
      if (query.route) search.set('route', query.route)
      if (query.limit !== undefined) search.set('limit', String(Math.trunc(query.limit)))
      const suffix = search.toString()
      return req<LogPage>(`/v1/projects/${id}/logs${suffix ? `?${suffix}` : ''}`)
    },
    listOAuthConnections: () => req<{ enabled: boolean; connections: OAuthConnection[] }>('/v1/me/connections'),
    narrowOAuthConnection: (id: string, projectId: string) => req<void>(`/v1/me/connections/${encodeURIComponent(id)}/project`, { method: 'POST', body: JSON.stringify({ projectId }) }),
    revokeOAuthConnection: (id: string) => req<void>(`/v1/me/connections/${encodeURIComponent(id)}`, { method: 'DELETE' }),
    listAccessTokens: () => req<{ tokens: AccessToken[] }>('/v1/me/tokens'),
    createAccessToken: (input: { name: string; expiresInDays?: number; scope?: 'full' | 'read_only' }) =>
      req<{ token: AccessToken & { plaintext: string }; warning: string }>('/v1/me/tokens', {
        method: 'POST', body: JSON.stringify(input),
      }),
    revokeAccessToken: (id: string) =>
      req<void>(`/v1/me/tokens/${id}`, { method: 'DELETE' }),
  }
}

/** One person who signed up to the customer's app. Never carries a hash. */
export type AuthorizationRequest = {
  organization_id: string
  client_id: string
  client_name: string
  scopes: string[]
  redirect_uri: string
  /** Resolved by the server in the reviewed workspace, never a client-supplied label. */
  project?: { id: string; name: string } | null
  /** False only for our own first-party clients, which skip the screen. */
  requires_consent: boolean
}

export type DeviceCodeRequest = {
  organization_id: string
  client_id: string
  client_name: string
  scopes: string[]
}

export type EndUser = {
  id: string
  email: string
  name: string | null
  emailVerified: boolean
  createdAt: string
  liveSessions: number
  /** Switched off: they cannot sign in until somebody turns it back on. */
  disabled: boolean
  providers: string[]
}

export type EndUserPage = {
  users: EndUser[]
  total: number
  /** Absent on the last page. Opaque - do not compute one. */
  nextCursor?: string
}

export type DbColumn = {
  name: string
  type: string
  primaryKey?: boolean
  notNull?: boolean
  unique?: boolean
  references?: { table: string; column: string }
}
export type DbTable = { name: string; columns: DbColumn[]; indexes?: { name: string; columns: string[]; unique?: boolean }[] }
export type DbSchema = { tables: DbTable[] }

export type SiteStatus = {
  deployed: boolean
  files?: number
  deployedAt?: string
  singlePageApp?: boolean
}

/** A model this project can call, as the catalogue describes it. */
export type ModelInfo = {
  name: string
  aliases: string[]
  describes: string
  contextTokens: number
  maxOutputTokens: number
}

/**
 * Whether a customer's own end users can sign up to this project.
 *
 * Nothing to do with signing in to this dashboard, which is Clerk and is
 * ours. This is the customer's app having its own users, kept in the
 * customer's own database.
 */
/** A social provider, as configured. The client secret is never returned. */
export type AuthProvider = {
  provider: 'google' | 'github'
  clientId: string
  /**
   * What has to be registered in the provider's console.
   *
   * Shown rather than documented: a mismatch here produces an error page at
   * Google that names neither the expected URL nor the sent one.
   */
  callbackUrl: string
  updatedAt: string
}

/**
 * A logical bucket: a registered prefix with a policy on it.
 *
 * The policy governs anon keys and signed-in users. It does NOT scope a
 * service key, which reads and writes everything.
 */
export type StorageBucket = {
  name: string
  visibility: 'private' | 'public'
  ownerScoped: boolean
  createdAt: string
}

export type AuthStatus =
  | { enabled: false; message?: string }
  | {
    enabled: true
    url: string
    providers: string[]
    social?: AuthProvider[]
    /** 'unavailable' means a forgotten password cannot be reset at all. */
    recovery?: 'email' | 'unavailable'
    /**
     * The CSRF allowlist as the platform has it recorded.
     *
     * Previously write-only: set once when auth was turned on and never
     * visible again, so somebody whose sign-in was being refused could not
     * see whether their origin was on the list.
     */
    trustedOrigins?: string[] | null
  }

/**
 * A custom domain, with the records still to be created.
 *
 * `instructions` travels with every response rather than behind a separate
 * call, because the question somebody has while a domain is connecting is
 * always "what do I type into my DNS provider" - and a second round trip
 * to answer it is a second chance to not answer it.
 */
export type CustomDomain = {
  id: string
  domain: string
  state: 'pending' | 'purchasing' | 'connecting' | 'dns_applied' | 'verified'
    | 'issuing_ssl' | 'active' | 'failed' | 'deleted'
  failureReason: string | null
  enteredAt: string
  stuck: boolean
  instructions: {
    records: { type: string; name: string; value: string; purpose: string }[]
    warnings: string[]
  }
}

export type EdgeFunction = {
  id: string
  name: string
  /**
   * A five-field cron expression in UTC, or null for request-only.
   *
   * The listing has carried this since functions shipped. This type did
   * not mention it, so it arrived on every page load and was discarded -
   * a function could be scheduled by an agent or the CLI and the dashboard
   * had no way to say so.
   */
  schedule?: string | null
  status: 'deploying' | 'live' | 'failed'
  version: number
  failureMessage: string | null
  updatedAt: string
}

/**
 * A secret as the dashboard may see it. There is no `value` field, because
 * there is no route that would populate one.
 */
export type Secret = {
  name: string
  hint: string
  updatedAt: string
}

/** What a write reports: which functions took the change, and which did not. */
export type SecretWrite = {
  redeployed: string[]
  stale: { name: string; message: string }[]
}

export type Policy = {
  id: number
  table: string
  role: 'anon' | 'authenticated'
  operation: 'select' | 'insert' | 'update' | 'delete'
  name: string
  using: unknown | null
  check: unknown | null
  createdAt: string
}

export type SqlResult = {
  results: Record<string, unknown>[]
  meta: { rowsRead?: number; rowsWritten?: number; changes?: number; durationMs?: number }
}

export type Finding = {
  severity: 'blocking' | 'likely' | 'note'
  code: string
  detail: string
  symptom?: string
  fix?: string
}
/**
 * One line of the request log.
 *
 * `sampleInterval` is the one field that changes what a count MEANS: a row
 * with an interval of 10 stands for ten requests. See lib/logs.ts.
 */
export type LogEntry = {
  timestamp: string
  level: 'info' | 'warn' | 'error'
  method: string
  route: string
  status: number
  durationMs: number
  outcome: string
  message: string | null
  statement: string | null
  credential: string
  rowsRead: number
  rowsWritten: number
  sampleInterval: number
}

/** `unavailable` is not "no logs": nobody will see a line until it is fixed. */
export type LogPage = { entries: LogEntry[]; unavailable?: { reason: string } }

export type Diagnosis = {
  project: { id: string; slug: string; status: string }
  summary: string
  findings: Finding[]
  checked: string[]
}

export type Api = ReturnType<typeof createApi>
